import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { FileUploadAnswer } from '../utils';

export type GenericFileUploadEndpoint = 'general' | 'tasks';

export interface GenericFileUploadRequest {
    endpoint: GenericFileUploadEndpoint;
    file: File;
    folder: string;
    database?: string;
    field?: string;
    fileField?: string;
}

@Injectable({
    providedIn: 'root'
})
export class GenericFileUploadService {
    private static readonly DEFAULT_GENERAL_FILE_FIELD = 'uploadFile';
    private static readonly DEFAULT_TASK_FILE_FIELD = 'fileToUpload';

    private readonly endpointByType: Record<GenericFileUploadEndpoint, string> = {
        general: 'ws/upload/upload_file_generic.php',
        tasks: 'ws/tareas/fileUploadGeneric.php',
    };

    constructor(private readonly http: HttpClient) {}

    /**
     * Sends every frontend file upload through one typed service.
     */
    public upload(request: GenericFileUploadRequest): Observable<FileUploadAnswer> {
        const formData = this.createFormData(request);
        const headers = new HttpHeaders({ Accept: 'application/json' });

        return this.http.post<FileUploadAnswer>(
            `${environment.serverUrl}${this.endpointByType[request.endpoint]}`,
            formData,
            { headers }
        );
    }

    /**
     * Uploads a generic image and returns the stored file name.
     */
    public uploadGeneralImage(file: File, folder: string): Observable<string> {
        return this.upload({ endpoint: 'general', file, folder }).pipe(
            map((response) => this.extractStoredFileName(response, folder))
        );
    }

    /**
     * Uploads a task file, usually a signature, and returns the stored file name.
     */
    public uploadTaskFile(
        file: File,
        folder: string,
        database: string,
        field: string,
    ): Observable<string> {
        return this.upload({ endpoint: 'tasks', file, folder, database, field }).pipe(
            map((response) => this.extractStoredFileName(response, folder))
        );
    }

    /**
     * Normalizes the different backend response shapes into one file name.
     */
    public extractStoredFileName(response: FileUploadAnswer, folder: string): string {
        const candidates = [
            response.fileName,
            response.file,
            response.target_file,
            response.aux_target_file,
        ];

        for (const candidate of candidates) {
            const fileName = this.extractFileName(candidate, folder);

            if (fileName.length > 0) {
                return fileName;
            }
        }

        return '';
    }

    private createFormData(request: GenericFileUploadRequest): FormData {
        const formData = new FormData();
        const fileField = request.fileField ?? this.getDefaultFileField(request.endpoint);

        formData.append(fileField, request.file, request.file.name);

        if (request.endpoint === 'tasks' && fileField !== GenericFileUploadService.DEFAULT_GENERAL_FILE_FIELD) {
            formData.append(GenericFileUploadService.DEFAULT_GENERAL_FILE_FIELD, request.file, request.file.name);
        }

        formData.append('folder', request.folder);

        if (request.database !== undefined && request.database.trim().length > 0) {
            formData.append('database', request.database);
        }

        if (request.field !== undefined && request.field.trim().length > 0) {
            formData.append('field', request.field);
        }

        return formData;
    }

    private getDefaultFileField(endpoint: GenericFileUploadEndpoint): string {
        return endpoint === 'tasks'
            ? GenericFileUploadService.DEFAULT_TASK_FILE_FIELD
            : GenericFileUploadService.DEFAULT_GENERAL_FILE_FIELD;
    }

    private extractFileName(value: string | undefined, folder: string): string {
        const rawValue = String(value ?? '').trim();

        if (rawValue.length === 0) {
            return '';
        }

        const normalizedValue = rawValue.replace(/\\/g, '/');
        const folderMarker = `/${folder}/`;
        const markerIndex = normalizedValue.lastIndexOf(folderMarker);

        if (markerIndex >= 0) {
            return normalizedValue.slice(markerIndex + folderMarker.length);
        }

        const slashIndex = normalizedValue.lastIndexOf('/');

        return slashIndex >= 0 ? normalizedValue.slice(slashIndex + 1) : normalizedValue;
    }
}
